import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import SafeIcon from '../common/SafeIcon';

export default function TermsOfService() {
  const lastUpdated = "December 1, 2025";
  
  const tocItems = [
    { id: 'agreement', title: 'Your Agreement to These Terms' },
    { id: 'definitions', title: 'Definitions' },
    { id: 'obligations', title: 'User Obligations and Prohibited Conduct' },
    { id: 'ip', title: 'Intellectual Property Rights' },
    { id: 'disclaimers', title: 'Disclaimers and Limitation of Liability' },
    { id: 'governing', title: 'Governing Law and Dispute Resolution' },
    { id: 'changes', title: 'Changes to Terms and Contact' }
  ];

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Handle smooth scroll for TOC to avoid HashRouter conflicts and account for fixed navbar
  const handleScrollToSection = (e, id) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      const navbarHeight = 80; // height of fixed navbar (h-20 = 80px)
      const y = element.getBoundingClientRect().top + window.scrollY - navbarHeight - 32; // 32px extra padding
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-dc-bg text-dc-text">
      <Navbar />
      
      <main className="flex-grow pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          {/* Legal Hero */}
          <div className="mb-16 text-center">
            <div className="inline-block bg-dc-teal-deep/5 text-dc-teal-signal text-xs font-michroma px-4 py-2 rounded-full mb-6 border border-dc-teal-signal/20">
              LEGAL
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Terms of Service</h1>
            <p className="text-lg text-dc-text/70 max-w-2xl mx-auto mb-4">
              This document outlines the legal agreement between RepUpgrade and its users. By accessing or using our services, you explicitly agree to be bound by these terms.
            </p>
            <p className="text-sm text-dc-text/50">
              Last updated: {lastUpdated}
            </p>
          </div>

          {/* Table of Contents */}
          <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-soft mb-16">
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
              <SafeIcon name="List" className="w-5 h-5 text-dc-teal-signal" />
              Table of Contents
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {tocItems.map((item, index) => (
                <a 
                  key={item.id}
                  href={`#${item.id}`}
                  onClick={(e) => handleScrollToSection(e, item.id)}
                  className="flex items-start gap-3 p-3 rounded-lg hover:bg-dc-bg transition-colors group cursor-pointer"
                >
                  <span className="text-dc-teal-signal font-michroma text-sm mt-1 group-hover:text-dc-teal-deep">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className="text-dc-text group-hover:text-dc-teal-signal font-medium">
                    {item.title}
                  </span>
                </a>
              ))}
            </div>
          </div>

          {/* Terms Sections */}
          <div className="space-y-16">
            <section id="agreement">
              <h2 className="text-2xl font-bold mb-6 text-dc-teal-deep border-l-4 border-dc-teal-signal pl-4 py-1">
                1. Your Agreement to These Terms
              </h2>
              <div className="space-y-4 text-dc-text/80 leading-relaxed">
                <p>
                  Welcome to RepUpgrade! By accessing or using our services, you agree to be bound by these Terms of Service. If you do not agree, please do not use our services. This version of the Terms of Service is effective as of {lastUpdated}.
                </p>
              </div>
            </section>

            <section id="definitions">
              <h2 className="text-2xl font-bold mb-6 text-dc-teal-deep border-l-4 border-dc-teal-signal pl-4 py-1">
                2. Definitions
              </h2>
              <div className="space-y-4 text-dc-text/80 leading-relaxed">
                <ul className="list-none space-y-4">
                  <li className="flex items-start gap-3">
                    <SafeIcon name="CheckCircle" className="w-5 h-5 text-dc-teal-signal shrink-0 mt-0.5" />
                    <div><span className="font-bold text-dc-text">Service:</span> Refers to the reputation management platform and related offerings provided by RepUpgrade.</div>
                  </li>
                  <li className="flex items-start gap-3">
                    <SafeIcon name="CheckCircle" className="w-5 h-5 text-dc-teal-signal shrink-0 mt-0.5" />
                    <div><span className="font-bold text-dc-text">User:</span> Any individual or entity accessing or using the RepUpgrade Service.</div>
                  </li>
                  <li className="flex items-start gap-3">
                    <SafeIcon name="CheckCircle" className="w-5 h-5 text-dc-teal-signal shrink-0 mt-0.5" />
                    <div><span className="font-bold text-dc-text">Content:</span> Includes all information, data, text, software, music, sound, photographs, graphics, video, messages, or other materials submitted, posted, or displayed by Users on or through the Service.</div>
                  </li>
                  <li className="flex items-start gap-3">
                    <SafeIcon name="CheckCircle" className="w-5 h-5 text-dc-teal-signal shrink-0 mt-0.5" />
                    <div><span className="font-bold text-dc-text">RepUpgrade:</span> Refers to the company RepUpgrade: Upgrade Your Reputation, and its associated website and platform.</div>
                  </li>
                </ul>
              </div>
            </section>

            <section id="obligations">
              <h2 className="text-2xl font-bold mb-6 text-dc-teal-deep border-l-4 border-dc-teal-signal pl-4 py-1">
                3. User Obligations and Prohibited Conduct
              </h2>
              <div className="space-y-8 text-dc-text/80 leading-relaxed">
                <div>
                  <h3 className="font-bold text-dc-text mb-4 text-lg">Your Responsibilities</h3>
                  <ul className="list-none space-y-3">
                    {[
                      "Provide accurate and current information during registration and while using our services.",
                      "Maintain the confidentiality and security of your account credentials.",
                      "Comply with all applicable local, national, and international laws and regulations.",
                      "Use RepUpgrade services only for lawful purposes and in a manner that does not infringe on the rights of others.",
                      "Promptly notify RepUpgrade of any unauthorized use of your account or any other breach of security."
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <div className="w-1.5 h-1.5 rounded-full bg-dc-teal-signal mt-2 shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="bg-red-50/50 p-6 rounded-xl border border-red-100">
                  <h3 className="font-bold text-red-800 mb-4 text-lg flex items-center gap-2">
                    <SafeIcon name="AlertTriangle" className="w-5 h-5" />
                    Prohibited Uses
                  </h3>
                  <ul className="list-none space-y-3 text-red-900/80">
                    {[
                      "Engaging in any fraudulent, deceptive, or misleading activity.",
                      "Harassing, threatening, or otherwise violating the legal rights of others.",
                      "Attempting to gain unauthorized access to any part of RepUpgrade's services, accounts, or computer systems.",
                      "Distributing or uploading any malicious software, viruses, or other harmful technological content.",
                      "Using our services to send unsolicited commercial messages ('spam') or to conduct pyramid schemes.",
                      "Interfering with or disrupting the integrity or performance of RepUpgrade's services or data.",
                      "Collecting or harvesting any personally identifiable information from the service without explicit permission."
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <div className="w-1.5 h-1.5 rounded-full bg-red-400 mt-2 shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </section>

            <section id="ip">
              <h2 className="text-2xl font-bold mb-6 text-dc-teal-deep border-l-4 border-dc-teal-signal pl-4 py-1">
                4. Intellectual Property Rights
              </h2>
              <div className="space-y-8 text-dc-text/80 leading-relaxed">
                <div>
                  <h3 className="font-bold text-dc-text mb-3 text-lg">RepUpgrade's Intellectual Property</h3>
                  <p>
                    All rights, title, and interest in and to the RepUpgrade service, including its software, design, algorithms, databases, code, visual interfaces, interactive features, graphics, content, and the compilation of all such elements, are and will remain the exclusive property of RepUpgrade and its licensors. Our services are protected by copyright, trademark, and other laws of both the United States and foreign countries.
                  </p>
                  <p className="mt-3">
                    The RepUpgrade name, the RepUpgrade logo, and all related names, logos, product and service names, designs, and slogans are trademarks of RepUpgrade or its affiliates or licensors. You must not use such marks without the prior written permission of RepUpgrade. All other names, logos, product and service names, designs, and slogans on this service are the trademarks of their respective owners.
                  </p>
                </div>
                
                <div>
                  <h3 className="font-bold text-dc-text mb-3 text-lg">User-Generated Content</h3>
                  <p>
                    You retain ownership of any intellectual property rights that you hold in content you submit, post, or display on or through the services. By submitting, posting, or displaying content, you grant RepUpgrade a worldwide, non-exclusive, royalty-free license (with the right to sublicense) to use, copy, reproduce, process, adapt, modify, publish, transmit, display, and distribute such content in any and all media or distribution methods (now known or later developed). This license is for the sole purpose of operating, promoting, and improving the services and developing new ones.
                  </p>
                  <p className="mt-3">
                    You agree that this license includes the right for RepUpgrade to provide, promote, and improve the services and to make content submitted to or through the services available to other companies, organizations, or individuals for the syndication, broadcast, distribution, promotion, or publication of such content on other media and services.
                  </p>
                </div>
                
                <div>
                  <h3 className="font-bold text-dc-text mb-3 text-lg">Third-Party Intellectual Property</h3>
                  <p>
                    RepUpgrade respects the intellectual property rights of others. Our services may include features or content that use third-party intellectual property. Any use of third-party intellectual property on our platform is either under license from the rightful owners or is permissible under applicable law (e.g., fair use). Users are responsible for ensuring that any content they upload or share does not infringe upon the intellectual property rights of others.
                  </p>
                </div>
              </div>
            </section>

            <section id="disclaimers">
              <h2 className="text-2xl font-bold mb-6 text-dc-teal-deep border-l-4 border-dc-teal-signal pl-4 py-1">
                5. Disclaimers and Limitation of Liability
              </h2>
              <div className="space-y-6 text-dc-text/80 leading-relaxed">
                <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 uppercase text-sm font-medium tracking-wide">
                  <h3 className="font-bold text-dc-text mb-3 text-base normal-case tracking-normal">Disclaimer of Warranties</h3>
                  <p className="mb-4">
                    Your use of the RepUpgrade service is at your sole risk. The service is provided on an "as is" and "as available" basis. RepUpgrade expressly disclaims all warranties of any kind, whether express or implied, including, but not limited to the implied warranties of merchantability, fitness for a particular purpose, and non-infringement. 
                  </p>
                  <p>
                    RepUpgrade makes no warranty that (i) the service will meet your requirements; (ii) the service will be uninterrupted, timely, secure, or error-free; (iii) the results that may be obtained from the use of the service will be accurate or reliable; or (iv) the quality of any products, services, information, or other material purchased or obtained by you through the service will meet your expectations.
                  </p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 uppercase text-sm font-medium tracking-wide">
                  <h3 className="font-bold text-dc-text mb-3 text-base normal-case tracking-normal">Limitation of Liability</h3>
                  <p className="mb-4">
                    You expressly understand and agree that RepUpgrade shall not be liable for any indirect, incidental, special, consequential, or exemplary damages, including but not limited to, damages for loss of profits, goodwill, use, data, or other intangible losses (even if RepUpgrade has been advised of the possibility of such damages), resulting from:
                  </p>
                  <ul className="list-decimal pl-6 mb-4 space-y-2">
                    <li>The use or the inability to use the service;</li>
                    <li>The cost of procurement of substitute goods and services resulting from any goods, data, information, or services purchased or obtained or messages received or transactions entered into through or from the service;</li>
                    <li>Unauthorized access to or alteration of your transmissions or data;</li>
                    <li>Statements or conduct of any third party on the service; or</li>
                    <li>Any other matter relating to the service.</li>
                  </ul>
                  <p>
                    In no event shall RepUpgrade's total liability to you for all damages, losses, and causes of action (whether in contract, tort (including but not limited to negligence), or otherwise) exceed the amount paid by you, if any, for accessing or participating in any activity related to the RepUpgrade service during the six (6) months immediately preceding the date of the claim or one hundred dollars ($100), whichever is greater.
                  </p>
                </div>
              </div>
            </section>

            <section id="governing">
              <h2 className="text-2xl font-bold mb-6 text-dc-teal-deep border-l-4 border-dc-teal-signal pl-4 py-1">
                6. Governing Law and Dispute Resolution
              </h2>
              <div className="space-y-6 text-dc-text/80 leading-relaxed">
                <div>
                  <h3 className="font-bold text-dc-text mb-3 text-lg">Governing Law</h3>
                  <p>
                    These Terms of Service and any disputes arising out of or in connection with them shall be governed by and construed in accordance with the laws of the State of Delaware, United States, without regard to its conflict of law principles. By using RepUpgrade services, you agree to submit to the exclusive jurisdiction of the courts located in Delaware for the resolution of any legal matters.
                  </p>
                  <p className="mt-3">
                    This choice of law does not, however, limit any mandatory consumer protection rights that you may be entitled to in your country of residence.
                  </p>
                </div>
                
                <div>
                  <h3 className="font-bold text-dc-text mb-3 text-lg">Dispute Resolution</h3>
                  <p>
                    In the event of any controversy or claim arising out of or relating to these Terms of Service, including any question regarding their existence, validity, or termination, the parties shall first attempt to resolve the dispute informally through good faith negotiations.
                  </p>
                  <p className="mt-3">
                    If informal negotiations fail to resolve the dispute within thirty (30) days, either party may elect to pursue non-binding mediation with a mutually agreed-upon mediator. The costs of mediation shall be shared equally between the parties.
                  </p>
                  <p className="mt-3">
                    Should mediation prove unsuccessful or if a party elects to bypass mediation, any remaining dispute, controversy, or claim shall be finally resolved by binding arbitration administered by the American Arbitration Association (AAA) in accordance with its Commercial Arbitration Rules. The arbitration shall take place in Delaware, United States.
                  </p>
                  <p className="mt-3">
                    The parties agree that any arbitration will be conducted on an individual basis and not as a class, consolidated, or representative action. The arbitrator's decision shall be final and binding and may be entered as a judgment in any court of competent jurisdiction.
                  </p>
                </div>
              </div>
            </section>

            <section id="changes">
              <h2 className="text-2xl font-bold mb-6 text-dc-teal-deep border-l-4 border-dc-teal-signal pl-4 py-1">
                7. Changes to Terms and Contact
              </h2>
              <div className="space-y-6 text-dc-text/80 leading-relaxed">
                <div>
                  <h3 className="font-bold text-dc-text mb-3 text-lg">Modifications to These Terms</h3>
                  <p>
                    RepUpgrade reserves the right to modify these Terms of Service at any time, in our sole discretion. We will notify users of any changes by posting the new Terms of Service on this page, and we may also notify you via email or through an in-service notification. Your continued use of the service after such modifications will constitute your acknowledgment and agreement to abide by the modified terms. We encourage you to review this page periodically for the latest information on our Terms.
                  </p>
                </div>
                
                <div>
                  <h3 className="font-bold text-dc-text mb-3 text-lg">Contact Information</h3>
                  <p>
                    If you have any questions or concerns regarding these Terms of Service, please do not hesitate to contact us. We are committed to clarity and transparency and will be happy to assist you.
                  </p>
                  
                  <div className="mt-6 bg-white rounded-xl p-6 border border-gray-100 inline-block">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-dc-teal-deep flex items-center justify-center">
                        <SafeIcon name="Mail" className="text-dc-gold w-6 h-6" />
                      </div>
                      <div>
                        <h4 className="font-bold text-dc-text">Email Support</h4>
                        <a href="mailto:support@repupgrade.com" className="text-dc-teal-signal hover:text-dc-teal-deep font-medium transition-colors">
                          support@repupgrade.com
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>

          {/* Back to Home CTA */}
          <div className="mt-16 pt-8 border-t border-gray-200">
            <div className="bg-dc-bg rounded-2xl p-8 text-center">
              <h2 className="text-2xl font-bold text-dc-text mb-4">Return to RepUpgrade</h2>
              <p className="text-dc-text/70 mb-6 max-w-2xl mx-auto">
                Back to building trust, content, and clear next steps for your local business.
              </p>
              <Link 
                to="/" 
                className="inline-flex items-center gap-2 bg-dc-orange hover:bg-dc-orange-hover text-white font-semibold py-3 px-6 rounded-lg transition-colors"
              >
                <SafeIcon name="ArrowLeft" className="w-4 h-4" />
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}